import { ScanLine, SlidersHorizontal, Undo2, Wallet, type LucideIcon } from "lucide-react";
import { LEDGER_LABEL, type LedgerRow } from "@/lib/karigarPoints";
import { useLanguage } from "@/i18n/language";

const KIND_ICON: Record<LedgerRow["kind"], LucideIcon> = {
  earn: ScanLine,
  redeem: Wallet,
  adjust: SlidersHorizontal,
  reverse: Undo2,
};

/**
 * Ledger rows, rendered the one way — home shows the last few, History shows
 * them all. Keeping the row here means the number on one screen can never be
 * formatted differently from the same number on the other.
 *
 * Each row leads with an icon for its kind, so a karigar can tell a scan from
 * a withdrawal at a glance, whichever language they read.
 */
export function LedgerList({ rows }: { rows: LedgerRow[] }) {
  const { pick } = useLanguage("hi");
  return (
    <ul className="divide-y divide-rule/70">
      {rows.map((row) => {
        const label = LEDGER_LABEL[row.kind];
        const credit = row.points >= 0;
        const Icon = KIND_ICON[row.kind] ?? ScanLine;
        return (
          <li className="flex items-center gap-3 px-4 py-3.5" key={row.id}>
            <span
              aria-hidden="true"
              className={`grid size-10 shrink-0 place-items-center rounded-full ${
                credit ? "bg-brand-light text-brand-ink" : "bg-ink/[0.06] text-muted"
              }`}
            >
              <Icon className="size-[1.125rem]" strokeWidth={2} />
            </span>

            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-ink">{pick(label)}</p>
              {row.product ? (
                <p className="mt-0.5 truncate text-[13px] text-ink/80">{row.product}</p>
              ) : null}
              {row.reason ? (
                <p className="mt-0.5 truncate text-xs text-muted">{row.reason}</p>
              ) : null}
              <p className="mt-0.5 text-xs text-muted/85">{formatWhen(row.at)}</p>
            </div>

            {/* The sign is carried by a character as well as a colour, so the
                row still reads correctly in bright sunlight or to someone who
                cannot tell the two greens apart. */}
            <p
              className={`shrink-0 font-title text-base font-semibold tabular-nums ${
                credit ? "text-success" : "text-ink"
              }`}
            >
              {credit ? "+" : "−"}
              {Math.abs(row.points)}
            </p>
          </li>
        );
      })}
    </ul>
  );
}

/** `en-IN` in both languages: the amounts beside it are Latin digits, and a
 *  Devanagari date next to them reads worse than a plain Indian one. */
function formatWhen(at: string): string {
  const d = new Date(at);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}
